import React from 'react';
import {
  Group,
  type GroupImperativeHandle,
  type Layout,
  useDefaultLayout,
} from 'react-resizable-panels';

import { readLegacyLayout } from './persistedLayout';

// v2 parity: hitAreaMargins {coarse: 15, fine: 5} around a 1px handle gave a
// total hit target of 31px (touch) / 11px (mouse); v4 expresses that as the
// minimum total size of the hit rect, configured on the group.
const RESIZE_TARGET_MINIMUM_SIZE = { coarse: 31, fine: 11 };

type ResizableGroupProps = {
  children: React.ReactNode;
  widthPersistKey?: string;
  /**
   * All panel ids the group can contain. Required when panels are
   * conditionally rendered so persisted layouts (including ones saved by the
   * pre-v4 library) restore for the full panel combination.
   */
  persistPanelIds?: string[];
  className?: string;
  /**
   * Persist only layouts the user produced. Groups that collapse a panel
   * programmatically need this so a collapsed layout is not saved as the
   * user's preferred one.
   */
  onlySaveAfterUserInteractions?: boolean;
  /**
   * Fires whenever the layout settles, including the first layout after the
   * group is measured; until then the imperative getLayout() returns {}.
   */
  onLayoutChanged?: (layout: Layout) => void;
};

const PersistedGroup = React.forwardRef<
  GroupImperativeHandle,
  ResizableGroupProps & { widthPersistKey: string }
>(
  (
    {
      children,
      widthPersistKey,
      persistPanelIds,
      className,
      onlySaveAfterUserInteractions,
      onLayoutChanged,
    },
    ref,
  ) => {
    const { defaultLayout, onLayoutChanged: persistLayout } = useDefaultLayout({
      id: widthPersistKey,
      panelIds: persistPanelIds,
      onlySaveAfterUserInteractions,
    });
    const legacyLayout = React.useMemo(
      () => readLegacyLayout(widthPersistKey, persistPanelIds),
      [widthPersistKey, persistPanelIds],
    );
    return (
      <Group
        groupRef={ref}
        orientation="horizontal"
        resizeTargetMinimumSize={RESIZE_TARGET_MINIMUM_SIZE}
        defaultLayout={defaultLayout ?? legacyLayout}
        onLayoutChanged={(layout, meta) => {
          persistLayout(layout, meta);
          onLayoutChanged?.(layout);
        }}
        className={className}
      >
        {children}
      </Group>
    );
  },
);
PersistedGroup.displayName = 'PersistedGroup';

// useDefaultLayout requires an id, so the persisted variant is a separate
// component rather than a conditional hook call.
export const ResizableGroup = React.forwardRef<
  GroupImperativeHandle,
  ResizableGroupProps
>((props, ref) =>
  props.widthPersistKey !== undefined ? (
    <PersistedGroup
      {...props}
      widthPersistKey={props.widthPersistKey}
      ref={ref}
    />
  ) : (
    <Group
      groupRef={ref}
      orientation="horizontal"
      resizeTargetMinimumSize={RESIZE_TARGET_MINIMUM_SIZE}
      onLayoutChanged={props.onLayoutChanged}
      className={props.className}
    >
      {props.children}
    </Group>
  ),
);
ResizableGroup.displayName = 'ResizableGroup';
